import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./scroll.css";

const SEARCH_API_ENDPOINT = `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/searchSongs`;
const ARABIC_PATTERN = /[\u0600-\u06FF]/;

const normalizeArabicText = (text) => {
  if (!text) return "";
  return text
    .replace(/[أإآا]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/[ئؤ]/g, (match) => (match === "ئ" ? "ي" : "و"))
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const createDebouncedFunction = (func, delay) => {
  let timeoutId;
  const debouncedFunction = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  debouncedFunction.cancel = () => clearTimeout(timeoutId);
  return debouncedFunction;
};

const SearchBar = () => {
  const { t, i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [textDirection, setTextDirection] = useState(
    i18n.language === "ar" ? "rtl" : "ltr"
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [cachedResults, setCachedResults] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const containsArabic = (text) => text && ARABIC_PATTERN.test(text);

  // Format search result for display
  const formatResult = (song, query) => {
    if (!song || !song.title) return null;

    // Use matchingSnippet if available, otherwise fallback to title
    let displayText = song.title;
    let snippet = "";

    // Check if we have a matchingSnippet from the API
    if (song.matchingSnippet && song.matchingSnippet.trim() !== "") {
      // Clean up the snippet (trim to reasonable length)
      snippet = song.matchingSnippet.trim();

      // If snippet is too long, truncate it
      if (snippet.length > 60) {
        snippet = snippet.substring(0, 60) + "...";
      }

      // Combine snippet with title
      displayText = `${snippet} (${song.title})`;
    }

    return {
      ...song,
      displayText,
      snippet: snippet || null,
      title: song.title,
    };
  };

  const fetchSearchResults = async (query) => {
    if (!query.trim()) return { results: [] };

    // Check cache first
    const cacheKey = query;
    if (cachedResults[cacheKey]) {
      console.log("Using cached results for:", cacheKey);
      return cachedResults[cacheKey];
    }

    try {
      setIsSearching(true);
      const url = `${SEARCH_API_ENDPOINT}?query=${encodeURIComponent(query)}`;
      console.log("Fetching URL:", url);

      const response = await fetch(url);
      console.log("Response Status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Raw Response Data:", data);

      const result = { results: data || [] };

      // Update cache
      setCachedResults((prevCache) => ({
        ...prevCache,
        [cacheKey]: result,
      }));

      return result;
    } catch (error) {
      console.error("Error fetching search results:", error);
      return { results: [] };
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    debounceRef.current = createDebouncedFunction(async (query) => {
      const trimmedQuery = query.trim();
      console.log("Debounced Query:", trimmedQuery);

      if (!trimmedQuery) {
        setSearchResults([]);
        setShowDropdown(false);
        setNoResultsFound(false);
        return;
      }

      const { results } = await fetchSearchResults(trimmedQuery);
      console.log("Fetched Results:", results);

      const processedResults = results
        .map((song) => formatResult(song, trimmedQuery))
        .filter((song) => song !== null);
      console.log("Processed Results:", processedResults);

      setSearchResults(processedResults);
      setShowDropdown(processedResults.length > 0);
      setNoResultsFound(processedResults.length === 0 && trimmedQuery !== "");
    }, 300);

    return () => {
      if (debounceRef.current) {
        debounceRef.current.cancel();
      }
    };
  }, []);

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    setTextDirection(
      i18n.language === "ar" || containsArabic(newValue) ? "rtl" : "ltr"
    );

    const trimmedValue = newValue.trim();
    if (!trimmedValue) {
      setSearchResults([]);
      setShowDropdown(false);
      setNoResultsFound(false);
      if (debounceRef.current) {
        debounceRef.current.cancel();
      }
      return;
    }

    if (debounceRef.current) {
      debounceRef.current(newValue);
    }
  };

  const clickSearchResult = (result) => {
    console.log(result);
    setShowDropdown(false);
    navigate(`/songs/${result.songID}`); // Adjusted to use _id instead of songID
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const SearchResult = ({ result }) => {
    if (!result.displayText) return null;

    const isArabic = containsArabic(result.displayText);

    // Determine if we have a snippet to show with title
    const hasSnippet = result.snippet && result.snippet.trim() !== "";

    return (
      <li
        className="cursor-pointer px-3 md:px-4 py-2 hover:bg-zinc-900 transition-all duration-200 ease-in-out"
        onClick={() => clickSearchResult(result)}
        style={{ direction: isArabic === "ar" ? "rtl" : "ltr" }}
      >
        {hasSnippet ? (
          <div
          >
            <div className="text-white text-sm md:text-base">
              <span className="text-amber-400">{result.snippet}</span>
              <span className="text-gray-400"> ... </span>
              <span className="text-white font-medium">({result.title})</span>
            </div>
          </div>
        ) : (
          <div
            className={`text-white text-sm md:text-base font-medium ${
              i18n.language === "ar" && isArabic ? "text-left font-arabic" : i18n.language === "ar" || isArabic ? "text-right font-arabic" : "text-left"
            }`}
          >
            {result.displayText}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="w-full px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mt-0">
          <div className="relative max-w-xl mx-auto w-full group">
            <div
              className={`
                absolute -inset-0.5 bg-gradient-to-r from-amber-500 via-orange-600 to-yellow-500
                rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-500
                ${isFocused ? "opacity-75" : ""}
              `}
            />
            <div className="relative">
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchValue.trim() && searchResults.length > 0)
                    setShowDropdown(true);
                  setIsFocused(true);
                }}
                onBlur={() => setIsFocused(false)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`
                  relative w-full text-white text-base md:text-lg
                  bg-black p-3 md:p-4
                  ${
                    textDirection === "rtl"
                      ? "px-10 md:pr-12 pl-4"
                      : "px-10 md:pl-12" 
                  }
                  rounded-xl transition-all duration-300 ease-in-out
                  hover:shadow-lg hover:bg-black
                  focus:ring-2 focus:ring-amber-500 focus:outline-none
                  ${isHovered ? "scale-[1.01]" : "scale-100"}
                  ${textDirection}
                `}
                style={{ direction: textDirection }}
              />
              <div
                className={`
                  absolute top-1/2 transform -translate-y-1/2
                  ${textDirection === "rtl" ? "right-3" : "left-3"}
                  transition-all duration-300 ease-in-out
                  ${
                    isHovered || isFocused
                      ? "text-amber-500 scale-110"
                      : "text-white"
                  }
                `}
              >
                {isSearching ? (
                  <svg
                    className="animate-spin h-4 w-4 md:h-5 md:w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <FaSearch className="w-4 h-4 md:w-5 md:h-5" />
                )}
              </div>
            </div>
            {showDropdown && searchResults.length > 0 && (
              <ul
                ref={dropdownRef}
                style={{ direction: textDirection }}
                className="absolute left-0 right-0 mt-2 bg-black border border-[#444545]
                  rounded-lg shadow-lg max-h-48 md:max-h-60 overflow-y-auto z-10
                  custom-scrollbar animate-in fade-in duration-200"
              >
                {searchResults.map((result, index) => (
                  <SearchResult
                    key={`${result._id}_${index}`}
                    result={result}
                  />
                ))}
              </ul>
            )}
            {noResultsFound && searchValue.trim() && (
              <div
                className="absolute left-0 right-0 mt-2 p-3 bg-black border border-[#444545]
                rounded-lg shadow-lg z-10 text-center text-gray-400 text-sm animate-in fade-in duration-200"
              >
                {t("search.noResults")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
